import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { FinanceCost, FinanceExpense, FinanceFrequency, FinanceReport } from '../lib/types';
import { useParent } from './ParentContext';

/** Admin → Finances (#398): the business's expenses and operating costs against subscription income, month by month. */

const money = (cents: number) => `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pad = (n: number) => String(n).padStart(2, '0');
const localDay = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shortDate = (ymd: string) => (ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const monthName = (ym: string) => new Date(`${ym}-15T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const shiftMonth = (ym: string, by: number) => {
  const d = new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1 + by, 15);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
/** "12.50" or "$1,200" → cents; NaN when it isn't an amount. */
const toCents = (s: string) => {
  const v = s.replace(/[$,\s]/g, '');
  return /^\d+(\.\d{0,2})?$/.test(v) ? Math.round(parseFloat(v) * 100) : NaN;
};
const toAmount = (cents: number) => (cents / 100).toFixed(2);
/** Time zones for the books (#745): which day a payout is dated and which month things count in. */
const US_ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'];
const ZONE_NAMES: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Phoenix': 'Arizona',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
};
const FREQ: Record<FinanceFrequency, string> = { daily: 'Daily', monthly: 'Monthly', yearly: 'Yearly' };
const PER: Record<FinanceFrequency, string> = { daily: 'a day', monthly: 'a month', yearly: 'a year' };

export function AdminFinances() {
  const { toast } = useParent();
  const [month, setMonth] = useState(() => localDay().slice(0, 7));
  const [report, setReport] = useState<FinanceReport | null>(null);
  const load = useCallback(() => {
    api
      .get<FinanceReport>(`/api/admin/finance?month=${month}&today=${localDay()}`)
      .then(setReport)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [month, toast]);
  useEffect(load, [load]);

  /** Run a change, then reload the month; false when it failed (the message is shown). */
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    try {
      await fn();
      if (done) toast({ text: done, tone: 'good' });
      load();
      return true;
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      return false;
    }
  };

  return (
    <div className="stack finance">
      <div className="finance-month row">
        <button className="btn small" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
          <ChevronLeft size={16} />
        </button>
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label="Month" />
        <button className="btn small" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">
          <ChevronRight size={16} />
        </button>
        {report?.timeZone && (
          <label className="finance-tz small">
            Time zone
            <select
              value={report.timeZone}
              onChange={(e) => void act(() => api.put('/api/admin/finance/settings', { timeZone: e.target.value }), 'Time zone saved.')}
              title="Which day a payout is dated and which month things count in"
            >
              {[...new Set([report.timeZone, ...US_ZONES])].map((z) => (
                <option key={z} value={z}>
                  {ZONE_NAMES[z] ?? z}
                </option>
              ))}
            </select>
          </label>
        )}
        {month !== localDay().slice(0, 7) && (
          <button className="btn small" onClick={() => setMonth(localDay().slice(0, 7))}>
            This month
          </button>
        )}
      </div>

      {!report ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="finance-kpis" role="list" aria-label={`${monthName(month)} totals`}>
            <Kpi label="Monthly subscription income" value={report.incomeCents} note={`${report.payingFamilies} paying ${report.payingFamilies === 1 ? 'family' : 'families'}${report.regularIncomeCents > report.incomeCents ? ` · ${money(report.regularIncomeCents)} once free periods end` : ''}`} />
            {report.billing && report.billing.active + report.billing.trialing + report.billing.pastDue + report.billing.ended > 0 && (
              <Kpi
                label="Billed through Stripe (monthly)"
                value={report.billing.mrrCents}
                note={[
                  `${report.billing.active} active`,
                  report.billing.trialing && `${report.billing.trialing} in trial`,
                  report.billing.pastDue && `${report.billing.pastDue} past due`,
                  report.billing.cancelling && `${report.billing.cancelling} cancelling`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                tone={report.billing.pastDue ? 'bad' : undefined}
              />
            )}
            <Kpi label={`Expenses in ${monthName(month)}`} value={report.expensesCents} note={`${report.expenses.length} ${report.expenses.length === 1 ? 'expense' : 'expenses'}`} />
            <Kpi label={`Operating costs in ${monthName(month)}`} value={report.operatingCents} note={`${report.costs.filter((c) => c.monthCents > 0).length} running`} />
            {report.ambassadors && (
              <Kpi
                label={`Brand Ambassador payouts in ${monthName(month)}`}
                value={report.ambassadors.paidCents}
                note={`${money(report.ambassadors.earnedCents)} earned this month · ${money(report.ambassadors.owedCents)} owed now`}
              />
            )}
            <Kpi label="Net for the month" value={report.netCents} note="Income − operating costs − expenses (ambassador payouts included)" tone={report.netCents < 0 ? 'bad' : 'good'} />
          </div>
          <p className="muted small">
            Income is what every family’s plan comes to per month right now. Yearly operating costs count as a twelfth each month;
            daily ones for each day they ran.
          </p>
          <Expenses month={month} items={report.expenses} act={act} />
          {report.ambassadors && <AmbassadorPayouts month={month} data={report.ambassadors} />}
          <OperatingCosts month={month} items={report.costs} act={act} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, note, tone }: { label: string; value: number; note: string; tone?: 'good' | 'bad' }) {
  return (
    <div className={`usage-tile finance-kpi ${tone ?? ''}`} role="listitem">
      <span className="muted small">{label}</span>
      <span className="usage-value">{money(value)}</span>
      <span className="muted small">{note}</span>
    </div>
  );
}

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;

// --- Brand Ambassador payouts (#723) ------------------------------------------------------------

/** What was paid to ambassadors this month, and what they're owed; payouts are marked in Admin → Partners. */
function AmbassadorPayouts({ month, data }: { month: string; data: NonNullable<FinanceReport['ambassadors']> }) {
  return (
    <section className="card stack">
      <div className="row-between wrap">
        <h2>Brand Ambassador payouts</h2>
        <a className="btn small" href="/admin/partners">
          Open Partners
        </a>
      </div>
      <p className="muted small">
        Earned in {monthName(month)}: {money(data.earnedCents)} · owed now: {money(data.owedCents)} · waiting out the hold: {money(data.waitingCents)}. Pay
        ambassadors with Stripe (or another way) in Admin → Partners; each payout is saved as an expense above, which counts it in the month’s net.
      </p>
      <p className="muted small">
        <strong>1099s:</strong> ambassadors paid through Stripe get their 1099-NEC from Stripe once tax reporting is on in your Stripe Dashboard
        (Connect → Tax forms).
        {data.outsideStripe && data.outsideStripe.length > 0
          ? ` Paid outside Stripe in ${data.year}, for your accountant: ${data.outsideStripe.map((o) => `${o.name} ${money(o.amountCents)}${o.methods ? ` (${o.methods})` : ''}`).join(' · ')}.`
          : ''}
      </p>
      {data.payouts.length === 0 ? (
        <p className="muted">No payouts in {monthName(month)}.</p>
      ) : (
        <div className="finance-table-scroll">
          <table className="finance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Ambassador</th>
                <th>Paid by</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((o, i) => (
                <tr key={i}>
                  <td>{new Date(o.paidAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                  <td>{o.name}</td>
                  <td>{o.method || '—'}</td>
                  <td className="num">{money(o.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- Expenses ------------------------------------------------------------------------------------

function Expenses({ month, items, act }: { month: string; items: FinanceExpense[]; act: Act }) {
  const blank = () => ({ date: localDay().slice(0, 7) === month ? localDay() : `${month}-01`, amount: '', memo: '' });
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    setForm(blank());
    setEditing(null);
    // A new month starts a fresh form dated in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  async function save() {
    const amountCents = toCents(form.amount);
    if (Number.isNaN(amountCents)) return void act(() => Promise.reject(new Error('Enter an amount, like 12.50.')));
    const body = { date: form.date, amountCents, memo: form.memo };
    const ok = await act(
      () => (editing ? api.put(`/api/admin/finance/expenses/${editing}`, body) : api.post('/api/admin/finance/expenses', body)),
      editing ? 'Expense saved.' : 'Expense added.',
    );
    if (ok) {
      setForm(blank());
      setEditing(null);
    }
  }

  return (
    <section className="card stack">
      <h2>Expenses</h2>
      <p className="muted small">One-off costs: what you paid, when, and what it was for.</p>
      <div className="finance-form">
        <label>
          Date
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </label>
        <label>
          Amount ($)
          <input inputMode="decimal" placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </label>
        <label className="finance-wide">
          Memo
          <input placeholder="What it was for" value={form.memo} maxLength={500} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
        </label>
      </div>
      <div className="row">
        <button className="btn primary" onClick={save} disabled={!form.date || !form.amount.trim() || !form.memo.trim()}>
          {editing ? 'Save expense' : (
            <>
              <Plus size={16} /> Add expense
            </>
          )}
        </button>
        {editing && (
          <button className="btn" onClick={() => (setEditing(null), setForm(blank()))}>
            Cancel
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="muted">No expenses in {monthName(month)}.</p>
      ) : (
        <div className="finance-table-scroll">
          <table className="finance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Memo</th>
                <th className="num">Amount</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className={editing === e.id ? 'editing' : ''}>
                  <td className="nowrap">{shortDate(e.date)}</td>
                  <td>
                    {e.memo}
                    {e.fromPayout && <span className="tag">Ambassador payout</span>}
                  </td>
                  <td className="num">{money(e.amountCents)}</td>
                  <td className="actions nowrap">
                    {e.fromPayout ? (
                      <span className="muted small" title="Kept in step with the payout in Admin → Partners">From Partners</span>
                    ) : (
                      <>
                    <button className="btn small" title="Edit" aria-label={`Edit ${e.memo}`} onClick={() => (setEditing(e.id), setForm({ date: e.date, amount: toAmount(e.amountCents), memo: e.memo }))}>
                      <Pencil size={15} />
                    </button>
                    <button
                      className="btn small danger-text"
                      title="Delete"
                      aria-label={`Delete ${e.memo}`}
                      onClick={() => confirm(`Delete “${e.memo}” (${money(e.amountCents)})?`) && act(() => api.del(`/api/admin/finance/expenses/${e.id}`), 'Expense deleted.')}
                    >
                      <Trash2 size={15} />
                    </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- Operating costs -----------------------------------------------------------------------------

function OperatingCosts({ month, items, act }: { month: string; items: FinanceCost[]; act: Act }) {
  const blank = () => ({ name: '', amount: '', frequency: 'monthly' as FinanceFrequency, startDate: localDay(), dueDate: '', endDate: '' });
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState<string | null>(null);
  const set = <K extends keyof ReturnType<typeof blank>>(k: K, v: ReturnType<typeof blank>[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    const amountCents = toCents(form.amount);
    if (Number.isNaN(amountCents)) return void act(() => Promise.reject(new Error('Enter an amount, like 24.00.')));
    const body = { name: form.name, amountCents, frequency: form.frequency, startDate: form.startDate, dueDate: form.dueDate, endDate: form.endDate };
    const ok = await act(
      () => (editing ? api.put(`/api/admin/finance/costs/${editing}`, body) : api.post('/api/admin/finance/costs', body)),
      editing ? 'Operating cost saved.' : 'Operating cost added.',
    );
    if (ok) {
      setForm(blank());
      setEditing(null);
    }
  }

  return (
    <section className="card stack">
      <h2>Operating costs</h2>
      <p className="muted small">Costs that repeat, like hosting, email and subscriptions: how much, how often, when it started and when it’s due. Fill in Stopped on only when it ends.</p>
      <div className="finance-form">
        <label className="finance-wide">
          What it is
          <input placeholder="e.g. Server hosting" value={form.name} maxLength={200} onChange={(e) => set('name', e.target.value)} />
        </label>
        <label>
          Amount ($)
          <input inputMode="decimal" placeholder="0.00" value={form.amount} onChange={(e) => set('amount', e.target.value)} />
        </label>
        <label>
          How often
          <select value={form.frequency} onChange={(e) => set('frequency', e.target.value as FinanceFrequency)}>
            {(Object.keys(FREQ) as FinanceFrequency[]).map((f) => (
              <option key={f} value={f}>
                {FREQ[f]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date started
          <input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
        </label>
        <label>
          Due date
          <input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
        </label>
        <label>
          Stopped on
          <input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
        </label>
      </div>
      <div className="row">
        <button className="btn primary" onClick={save} disabled={!form.name.trim() || !form.amount.trim() || !form.startDate}>
          {editing ? 'Save operating cost' : (
            <>
              <Plus size={16} /> Add operating cost
            </>
          )}
        </button>
        {editing && (
          <button className="btn" onClick={() => (setEditing(null), setForm(blank()))}>
            Cancel
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="muted">No operating costs yet.</p>
      ) : (
        <div className="finance-table-scroll">
          <table className="finance-table">
            <thead>
              <tr>
                <th>What</th>
                <th className="num">Amount</th>
                <th className="num">In {monthName(month)}</th>
                <th>Started</th>
                <th>Next due</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className={editing === c.id ? 'editing' : ''}>
                  <td>
                    {c.name}
                    {c.endDate && <span className="muted small block">Stopped {shortDate(c.endDate)}</span>}
                  </td>
                  <td className="num nowrap">
                    {money(c.amountCents)} <span className="muted small">{PER[c.frequency]}</span>
                  </td>
                  <td className="num">{money(c.monthCents)}</td>
                  <td className="nowrap">{shortDate(c.startDate)}</td>
                  <td className="nowrap">{shortDate(c.nextDue)}</td>
                  <td className="actions nowrap">
                    <button
                      className="btn small"
                      title="Edit"
                      aria-label={`Edit ${c.name}`}
                      onClick={() => (
                        setEditing(c.id),
                        setForm({ name: c.name, amount: toAmount(c.amountCents), frequency: c.frequency, startDate: c.startDate, dueDate: c.dueDate, endDate: c.endDate })
                      )}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      className="btn small danger-text"
                      title="Delete"
                      aria-label={`Delete ${c.name}`}
                      onClick={() =>
                        confirm(`Delete “${c.name}”? It disappears from every month. To keep its history, set the day it stopped instead.`) &&
                        act(() => api.del(`/api/admin/finance/costs/${c.id}`), 'Operating cost deleted.')
                      }
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
